import React, { useMemo } from 'react';
import Circle from './Circle';
import Canvas from './Canvas';

const MainContent = ({
  circles,
  categories,
  activeColor,
  activeCircles,
  activeAddOns,
  onCircleClick,
  imageUrl,
  viewMode,
  diagramImageUrl,
  isPlatformViewActive,
  onPlatformToggle,
  platformImage,
  circlePositions,
  selectedProduct
}) => {
  const allAnimatedCircles = useMemo(() => [...activeCircles, ...activeAddOns], [activeCircles, activeAddOns]);
  
  // This unique key changes with every selection, forcing the SVG to re-render and re-animate.
  const animationKey = useMemo(() => allAnimatedCircles.join('-'), [allAnimatedCircles]);

  const layoutData = useMemo(() => {
    const totalCircles = circles.length;
    if (totalCircles === 0 || !categories || categories.length === 0) {
      return { gradient: '', labels: [] };
    }
    let cumulativePercentage = 0;
    const gradientStops = [];
    const labels = [];
    const categoryCounts = categories.map(cat => ({
      ...cat,
      count: circles.filter(c => c.category === cat.name).length
    }));
    categoryCounts.forEach(cat => {
      const percentage = (cat.count / totalCircles) * 100;
      if (percentage === 0) return;
      const startPercentage = cumulativePercentage;
      const endPercentage = startPercentage + percentage;
      gradientStops.push(`${cat.color} ${startPercentage}% ${endPercentage}%`);
      labels.push({
        name: cat.name.toUpperCase(),
        offset: `${startPercentage + (percentage / 2)}%`
      });
      cumulativePercentage = endPercentage;
    });
    const gradient = `conic-gradient(${gradientStops.join(', ')})`;
    return { gradient, labels };
  }, [circles, categories]);

  const containerSize = 800;

  const renderContent = () => {
    if (isPlatformViewActive) {
      return (
        <div className="diagram-view-container">
          <img src={platformImage} alt="Platform Overview" />
        </div>
      );
    }
    if (viewMode === 'diagram') {
      return (
        <div className="diagram-view-container">
          <img src={diagramImageUrl} alt="Diagram" />
        </div>
      );
    }
    return (
      <div className="circle-container">
        <div className="gradient-ring" style={{ background: layoutData.gradient }}></div>
        <div className="labels-container">
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path id="curve" fill="none" d="M 50, 5 a 45,45 0 1,1 0,90 a 45,45 0 1,1 0,-90" />
            {layoutData.labels.map(label => (
              <text key={label.name}>
                <textPath href="#curve" startOffset={label.offset} textAnchor="middle">
                  {label.name}
                </textPath>
              </text>
            ))}
          </svg>
        </div>
        <svg key={animationKey} className="animation-layer" width={containerSize} height={containerSize}>
          {allAnimatedCircles.map(label => {
            const pos = circlePositions.get(label);
            if (!pos) return null;
            const center = containerSize / 2;
            const x1 = center + pos.x;
            const y1 = center + pos.y;
            const lineLength = Math.sqrt(pos.x * pos.x + pos.y * pos.y);
            return (
              <line
                key={label}
                x1={x1}
                y1={y1}
                x2={center}
                y2={center}
                className="anim-line"
                strokeDasharray={lineLength}
                strokeDashoffset={lineLength}
              />
            );
          })}
        </svg>
        <Canvas
          imageUrl={imageUrl}
          selectedProduct={selectedProduct}
        />
        {circles.map((circle, index) => {
          const pos = circlePositions.get(circle.label);
          const style = pos ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : {};
          const isPrimaryActive = activeCircles.includes(circle.label);
          const isAddOnActive = activeAddOns.includes(circle.label);
          return (
            <Circle
              key={index}
              label={circle.label}
              isActive={isPrimaryActive}
              isAddOn={isAddOnActive}
              onClick={() => onCircleClick(circle.label)}
              style={style}
              baseColor={circle.baseColor}
              activeColor={activeColor}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="main-content">
      <div className="platform-toggle" onClick={onPlatformToggle}>
        {isPlatformViewActive ? 'Ecosystem Toggle' : 'Platform Toggle'}
      </div>
      {renderContent()}
    </div>
  );
};

export default MainContent;